import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import c0Img from "../../../src/assets/images/C0_others.svg";
import c1Img from "../../../src/assets/images/C1_services.svg";
import c2Img from "../../../src/assets/images/C2_spares.svg";
import c3Img from "../../../src/assets/images/C3_acillaries.svg";
import c4Img from "../../../src/assets/images/C4_supplyChain.svg";
import { fetchDelayResonCount } from "../../../src/pages/servicePerformance/kpiService";
import { FilterContext } from "../../context/Filter";

const Delayreasoncard = React.memo(({ selectedMonth }) => {
  const [delayResonCount, setDelayResonCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const { open, setOpen } = useContext(FilterContext)

  const { superArn: parArn, childArn, isFilterReady } = useContext(FilterContext);

  const fetchDelayResonCountData = useCallback(async (parArn, childArn, months) => {
    try {
      setLoading(true);
      if (!months || months.length === 0) {
        console.warn("Skipping API call: selectedMonth is empty");
        setDelayResonCount([]); // Reset data
        setLoading(false);
        return;
      }

      const apiData = await fetchDelayResonCount(parArn, childArn, months);

      if (!apiData) {
        console.error("API response is empty or invalid:", apiData);
        setDelayResonCount([]);
        return;
      }

      let delayReasonBuckets = [];

      if (apiData?.length === 1) {
        // Single month → take buckets directly
        delayReasonBuckets = apiData[0]?.delay_reason_count?.buckets || [];
      } else if (apiData?.length > 1) {
        // Multiple months → merge buckets by key
        const merged = {};
        apiData.forEach((monthData) => {
          monthData?.delay_reason_count?.buckets?.forEach((bucket) => {
            const key = bucket?.key;
            const count = bucket?.doc_count || 0;
            if (!merged[key]) {
              merged[key] = { key, doc_count: 0 };
            }
            merged[key].doc_count += count;
          });
        });
        delayReasonBuckets = Object.values(merged);
      }

      setDelayResonCount(delayReasonBuckets);
    } catch (error) {
      console.error("Error fetching complaint data:", error);
      setDelayResonCount([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoize the getDocCount function to prevent recalculations
  const getDocCount = useCallback((key) => {
    const item = delayResonCount?.find((i) => i.key === key);
    return item ? item.doc_count : 0;
  }, [delayResonCount]);

  // Convert selectedMonth to string for stable comparison
  const selectedMonthKey = useMemo(() => {
    return Array.isArray(selectedMonth) ? selectedMonth.join(',') : '';
  }, [selectedMonth]);

  useEffect(() => {
    // Only call API if valid selectedMonth is provided
    if (selectedMonth && selectedMonth.length > 0) {
      fetchDelayResonCountData(parArn, childArn, selectedMonth);
    } else {
      // Reset data if no months selected
      setDelayResonCount([]);
      setLoading(false);
    }
  }, [selectedMonthKey, parArn, childArn, fetchDelayResonCountData]);

  // Memoize the doc counts to prevent recalculation on every render
  const docCounts = useMemo(() => ({
    supplyChain: getDocCount("C0 (Supply Chain)"),
    service: getDocCount("C1 (Service)"),
    spares: getDocCount("C2 (Spares)"),
    ancillaries: getDocCount("C3 (Ancillaries)"),
    other: getDocCount("Other")
  }), [getDocCount]);

  return (
    <div>
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : (
        <div className="flex-col m-2">
          <div className="block w-full md:flex gap-1">
            <div className="bg-[#FFE3BA] flex w-full xl:w-1/3 justify-between items-center border p-2 rounded-md">
              <img className="size-20" src={c0Img} alt="Supply Chain" />
              <div className="justify-center">
                <h1 className="text-3xl text-center">
                  {docCounts.supplyChain}
                </h1>
                <h1 className="text-sm">Supply Chain</h1>
              </div>
            </div>
            <div className="bg-[#D0EBFF] flex w-full xl:w-1/3 justify-between items-center border p-2 rounded-md">
              <img className="size-20" src={c1Img} alt="Service" />
              <div className="justify-center">
                <h1 className="text-3xl">{docCounts.service}</h1>
                <h1 className="text-sm">Service</h1>
              </div>
            </div>
            <div className="bg-[#C6FFE7] w-full xl:w-1/3 flex p-2 items-center justify-between border rounded-md">
              <img className="size-20" src={c2Img} alt="Spares" />
              <div>
                <h1 className="text-3xl">{docCounts.spares}</h1>
                <h1 className="text-sm">Spares</h1>
              </div>
            </div>
          </div>
          <div className="flex gap-1 mt-1">
            <div className="bg-[#C8FAFF] flex w-1/2 justify-between items-center p-3 border rounded-md">
              <img className="size-20" src={c3Img} alt="Ancillaries" />
              <div>
                <h1 className="text-3xl">{docCounts.ancillaries}</h1>
                <h1 className="text-sm">Ancillaries</h1>
              </div>
            </div>
            <div className="bg-[#3DC293] w-1/2 flex justify-between items-center p-3 border rounded-md">
              <img className="size-20" src={c4Img} alt="Others" />
              <div>
                <h1 className="text-3xl">{docCounts.other}</h1>
                <h1 className="text-sm">Others</h1>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Delayreasoncard.displayName = 'Delayreasoncard';

export default Delayreasoncard;